/**
 * Which imported features a click landed on.
 *
 * WHY THIS EXISTS
 * A WMS layer answers a click with GetFeatureInfo and an ArcGIS layer with
 * `query` — both ask a server "what is at this point". A layer imported from a
 * file has no server to ask, so the pop-up used to be switched off for it
 * entirely: `capabilities.ts` said `popup: false` because "the identify path
 * never queries it". This is the missing query, run against the features
 * already in the browser.
 *
 * MEASURED IN PIXELS, NOT DEGREES
 * The tolerance for "close enough to a line" has to mean the same thing to the
 * user at every zoom level, and a degree does not: 0.0001° is a comfortable
 * target at street zoom and an invisible sliver at country zoom. Converting to
 * layer points first makes the tolerance exactly what it looks like on screen.
 *
 * Polygons are the exception and are tested in lon/lat, because containment is
 * scale-free — a point is inside a ward or it is not.
 */

import type OLMap from 'ol/Map';
import { fromLonLat } from 'ol/proj';

/** How near a click has to be to a point or line to count as hitting it. */
export const HIT_TOLERANCE_PX = 8;

type Ring = [number, number][];

/**
 * Ray casting, honouring holes.
 *
 * A point inside a hole is inside the outer ring and inside the hole, which
 * cancels out to "outside" — which is what an island in a lake should do.
 */
function pointInRings(x: number, y: number, rings: Ring[]): boolean {
  let inside = false;
  for (const ring of rings) {
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
      const [xi, yi] = ring[i];
      const [xj, yj] = ring[j];
      const straddles = yi > y !== yj > y;
      if (!straddles) continue;
      const crossesLeft = x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
      if (crossesLeft) inside = !inside;
    }
  }
  return inside;
}

function polygonsOf(geometry: GeoJSON.Geometry): Ring[][] {
  if (geometry.type === 'Polygon') return [geometry.coordinates as Ring[]];
  if (geometry.type === 'MultiPolygon') return geometry.coordinates as Ring[][];
  return [];
}

/** Shortest distance from a point to a segment, in the same units as the input. */
function distanceToSegment(
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
): number {
  const dx = bx - ax;
  const dy = by - ay;
  if (dx === 0 && dy === 0) return Math.hypot(px - ax, py - ay);

  // How far along the segment the nearest point lies, clamped to its ends so a
  // click beyond either end measures to that end rather than to the infinite line.
  const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / (dx * dx + dy * dy)));
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}

/** Every coordinate pair in a geometry, whatever its nesting. */
function* coordinatePairs(geometry: GeoJSON.Geometry): Generator<[number, number]> {
  const walk = function* (value: any): Generator<[number, number]> {
    if (!Array.isArray(value)) return;
    if (typeof value[0] === 'number' && typeof value[1] === 'number') {
      yield [value[0], value[1]];
      return;
    }
    for (const part of value) yield* walk(part);
  };
  yield* walk((geometry as any).coordinates);
}

/** Every straight run in a geometry, as coordinate pairs. */
function* segments(geometry: GeoJSON.Geometry): Generator<[[number, number], [number, number]]> {
  const walkLine = function* (line: any): Generator<[[number, number], [number, number]]> {
    if (!Array.isArray(line) || line.length < 2) return;
    for (let i = 1; i < line.length; i += 1) {
      const a = line[i - 1];
      const b = line[i];
      if (typeof a?.[0] === 'number' && typeof b?.[0] === 'number') {
        yield [
          [a[0], a[1]],
          [b[0], b[1]],
        ];
      }
    }
  };

  const walk = function* (value: any, depth: number): Generator<[[number, number], [number, number]]> {
    if (!Array.isArray(value)) return;
    // A depth whose children are coordinate pairs is a line.
    if (Array.isArray(value[0]) && typeof value[0][0] === 'number') {
      yield* walkLine(value);
      return;
    }
    for (const part of value) yield* walk(part, depth + 1);
  };

  yield* walk((geometry as any).coordinates, 0);
}

/**
 * The features under a click, nearest last.
 *
 * Ordered so the caller can treat the END of the list as the topmost hit,
 * matching the map's own draw order where later layers paint over earlier
 * ones. Pixel-space tolerance for points/lines, scale-free lon/lat
 * containment for polygons — screen pixels come from
 * `map.getPixelFromCoordinate`, fed EPSG:3857 coordinates since that's the
 * projection the map is actually drawn in.
 */
export function hitTestFeaturesOL(
  map: OLMap,
  lonLat: [number, number],
  features: GeoJSON.Feature[],
  tolerancePx: number = HIT_TOLERANCE_PX,
): GeoJSON.Feature[] {
  const clickPoint = map.getPixelFromCoordinate(fromLonLat(lonLat));
  if (!clickPoint) return [];
  const toPoint = (lon: number, lat: number) => map.getPixelFromCoordinate(fromLonLat([lon, lat]));

  const hits: GeoJSON.Feature[] = [];

  for (const feature of features) {
    const geometry = feature.geometry;
    if (!geometry || !(geometry as any).coordinates) continue;

    const type = geometry.type;

    if (type === 'Polygon' || type === 'MultiPolygon') {
      const inside = polygonsOf(geometry).some((rings) => pointInRings(lonLat[0], lonLat[1], rings));
      if (inside) {
        hits.push(feature);
        continue;
      }
    }

    if (type === 'Point' || type === 'MultiPoint') {
      let near = false;
      for (const [lon, lat] of coordinatePairs(geometry)) {
        const point = toPoint(lon, lat);
        if (point && Math.hypot(clickPoint[0] - point[0], clickPoint[1] - point[1]) <= tolerancePx) {
          near = true;
          break;
        }
      }
      if (near) hits.push(feature);
      continue;
    }

    let near = false;
    for (const [[ax, ay], [bx, by]] of segments(geometry)) {
      const a = toPoint(ax, ay);
      const b = toPoint(bx, by);
      if (a && b && distanceToSegment(clickPoint[0], clickPoint[1], a[0], a[1], b[0], b[1]) <= tolerancePx) {
        near = true;
        break;
      }
    }
    if (near) hits.push(feature);
  }

  return hits;
}
